import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Lock, ShieldCheck } from 'lucide-react';
import { toast } from 'sonner';
import api, { formatApiError } from '../lib/api';

export const ChangePassword = () => {
  const { user, refreshMe } = useAuth();
  const navigate = useNavigate();

  const [current, setCurrent] = useState('');
  const [pw1, setPw1] = useState('');
  const [pw2, setPw2] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const mustChange = user && user.must_change_password;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (pw1.length < 8) return setError('New password must be at least 8 characters.');
    if (pw1 !== pw2) return setError('New passwords do not match.');
    if (pw1 === current) return setError('New password must be different from your current password.');
    setSubmitting(true);
    try {
      await api.post('/auth/change-password', { current_password: current, new_password: pw1 });
      toast.success('Password updated successfully');
      await refreshMe();
      navigate('/', { replace: true });
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-6 bg-slate-50" data-testid="change-password-page">
      <Card className="w-full max-w-md border-slate-200 shadow-sm">
        <CardContent className="p-8 space-y-6">
          <div className="flex justify-center mb-2">
            <img src="/csg-logo.png" alt="CSG Security" className="h-10 w-auto" />
          </div>

          {mustChange && (
            <div className="flex items-start gap-3 bg-amber-50 border border-amber-200 rounded-lg p-3" data-testid="must-change-notice">
              <ShieldCheck className="w-5 h-5 text-amber-600 mt-0.5" />
              <div>
                <p className="text-sm font-semibold text-amber-900">Set your password</p>
                <p className="text-xs text-amber-800 mt-0.5 leading-relaxed">
                  For security, you must change your temporary password before continuing.
                </p>
              </div>
            </div>
          )}

          <div>
            <h2 className="text-2xl font-heading font-bold text-slate-900">
              {mustChange ? 'Choose a new password' : 'Change your password'}
            </h2>
            <p className="text-sm text-slate-500 mt-1">
              Use at least 8 characters. Mix letters, numbers, and symbols for best security.
            </p>
          </div>

          {error && (
            <Alert variant="destructive" data-testid="change-password-error">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="current" className="text-xs uppercase tracking-wide text-slate-600">
                {mustChange ? 'Temporary password' : 'Current password'}
              </Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  id="current"
                  type="password"
                  required
                  autoFocus
                  value={current}
                  onChange={(e) => setCurrent(e.target.value)}
                  className="pl-9"
                  data-testid="current-password-input"
                />
              </div>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="pw1" className="text-xs uppercase tracking-wide text-slate-600">
                New password
              </Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  id="pw1"
                  type="password"
                  required
                  minLength={8}
                  value={pw1}
                  onChange={(e) => setPw1(e.target.value)}
                  className="pl-9"
                  data-testid="new-password-input"
                />
              </div>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="pw2" className="text-xs uppercase tracking-wide text-slate-600">
                Confirm new password
              </Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  id="pw2"
                  type="password"
                  required
                  minLength={8}
                  value={pw2}
                  onChange={(e) => setPw2(e.target.value)}
                  className="pl-9"
                  data-testid="confirm-new-password-input"
                />
              </div>
            </div>

            <Button
              type="submit"
              className="w-full bg-[#80C030] hover:bg-[#6FA528] text-white font-semibold"
              disabled={submitting}
              data-testid="change-password-submit-btn"
            >
              {submitting ? 'Updating...' : 'Update password'}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};
